import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";

interface FileUploadError {
  code:
    | "file-invalid-type"
    | "file-too-large"
    | "file-too-small"
    | "too-many-files";
  message: string;
}

interface FileUploadRejection {
  file: File;
  errors: FileUploadError[];
}

interface FileUploadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value: File[];
  onChange: (files: File[]) => void;
  accept?: string;
  description?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  fileTypeName?: string;
  maxFiles?: number;
  maxSize?: number;
  minSize?: number;
  multiple?: boolean;
  onReject?: (rejections: FileUploadRejection[]) => void;
  renderFooter?: React.ReactNode;
  showRemove?: boolean;
  asChild?: boolean;
}

const getAcceptAttribute = (accept?: string) => accept;

const isFileAccepted = (file: File, accept?: string) => {
  if (!accept) return true;

  const list = accept.split(",").map((item) => item.trim().toLowerCase());
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();

  return list.some((item) => {
    if (item.startsWith(".")) {
      return name.endsWith(item);
    }
    if (item.endsWith("/*")) {
      const prefix = item.slice(0, -1);
      return type.startsWith(prefix);
    }
    return type === item;
  });
};

export const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      value,
      onChange,
      accept,
      description,
      disabled = false,
      errorMessage: errorMessageOverride,
      fileTypeName,
      maxFiles,
      maxSize,
      minSize,
      multiple = false,
      onReject,
      renderFooter,
      showRemove = true,
      asChild = false,
      className,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [dragCounter, setDragCounter] = React.useState(0);
    const [isDragReject, setIsDragReject] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const isDragActive = dragCounter > 0;
    const acceptAttr = React.useMemo(
      () => getAcceptAttribute(accept),
      [accept],
    );

    const formatRejectionMessage = React.useCallback(
      (rejections: FileUploadRejection[]) => {
        if (rejections.length === 0) return null;

        const [firstRejection] = rejections;
        const [firstError] = firstRejection.errors;

        if (!firstError) return null;

        if (firstError.code === "too-many-files") {
          return firstError.message;
        }

        if (rejections.length === 1) {
          return `${firstRejection.file.name}: ${firstError.message}`;
        }

        const uniqueMessages = new Set(
          rejections.flatMap((rejection) =>
            rejection.errors.map((error) => error.message),
          ),
        );
        if (uniqueMessages.size === 1) {
          return `${rejections.length} files rejected. ${Array.from(uniqueMessages)[0]}`;
        }

        return `${rejections.length} files rejected.`;
      },
      [],
    );

    const validateFiles = React.useCallback(
      (files: File[]) => {
        if (disabled) return { accepted: [], rejected: [] };

        const rejected: FileUploadRejection[] = [];
        const accepted: File[] = [];

        if (maxFiles && files.length > maxFiles) {
          const error: FileUploadError = {
            code: "too-many-files",
            message: `Please select no more than ${maxFiles} file${
              maxFiles === 1 ? "" : "s"
            }.`,
          };
          files.forEach((file) => rejected.push({ file, errors: [error] }));
          return { accepted, rejected };
        }

        files.forEach((file) => {
          const errors: FileUploadError[] = [];

          if (accept && !isFileAccepted(file, accept)) {
            errors.push({
              code: "file-invalid-type",
              message: fileTypeName
                ? `Please select a valid ${fileTypeName} file.`
                : "Invalid file type.",
            });
          }

          if (typeof maxSize === "number" && file.size > maxSize) {
            errors.push({
              code: "file-too-large",
              message: "File is too large.",
            });
          }

          if (typeof minSize === "number" && file.size < minSize) {
            errors.push({
              code: "file-too-small",
              message: "File is too small.",
            });
          }

          if (errors.length > 0) {
            rejected.push({ file, errors });
          } else {
            accepted.push(file);
          }
        });

        return { accepted, rejected };
      },
      [accept, disabled, fileTypeName, maxFiles, maxSize, minSize],
    );

    const handleFiles = React.useCallback(
      (files: File[]) => {
        const { accepted, rejected } = validateFiles(files);

        if (rejected.length > 0) {
          setIsDragReject(true);
          setErrorMessage(formatRejectionMessage(rejected));
          onReject?.(rejected);
        } else {
          setIsDragReject(false);
          setErrorMessage(null);
        }

        if (accepted.length === 0) return;

        const selectedFiles = multiple ? accepted : accepted.slice(0, 1);
        onChange(multiple ? [...value, ...selectedFiles] : selectedFiles);
      },
      [
        formatRejectionMessage,
        multiple,
        onChange,
        onReject,
        validateFiles,
        value,
      ],
    );

    const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      setIsDragReject(false);
      setErrorMessage(null);
      setDragCounter((prev) => prev + 1);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      setDragCounter((prev) => Math.max(0, prev - 1));
    };

    const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      setDragCounter(0);
      const files = Array.from(event.dataTransfer.files);
      handleFiles(files);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      handleFiles(files);
      event.target.value = "";
    };

    const open = () => {
      if (disabled) return;
      inputRef.current?.click();
    };

    const resolvedErrorMessage = errorMessageOverride ?? errorMessage;
    const hasFiles = value.length > 0;
    const hasError = Boolean(resolvedErrorMessage);
    const showSuccess = hasFiles && !hasError;
    const label = multiple
      ? hasFiles
        ? "Select Another"
        : "Choose Files"
      : hasFiles
        ? "Change File"
        : "Choose File";
    const fileLabel = multiple
      ? hasFiles
        ? `${value.length} file${value.length === 1 ? "" : "s"} selected`
        : "No files selected"
      : (value[0]?.name ?? "No file selected");
    const typeLabel = fileTypeName ?? "file";
    const state =
      isDragReject || Boolean(errorMessageOverride)
        ? "reject"
        : isDragActive
          ? "active"
          : "idle";
    const Comp = asChild ? Slot : "div";

    return (
      <Comp ref={ref} className={cn(className)} {...props}>
        <Stack gap="lg" fullWidth>
          <Stack
            direction="column"
            gap="none"
            data-state={state}
            data-disabled={disabled ? "true" : undefined}
            className={cn(
              "w-full min-w-[320px] rounded-lg border-2 border-dashed p-6 transition-colors",
              "data-[state=active]:border-primary data-[state=active]:bg-primary/5",
              "data-[state=reject]:border-red-50 data-[state=reject]:bg-red-5",
              "data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-60",
              showSuccess && "border-green-40 bg-green-5",
            )}
            onClick={() => {
              if (!disabled) open();
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <Stack gap="md" align="center" fullWidth>
              <DocumentArrowUpIcon
                className={cn(
                  "h-12 w-12 text-gray-400",
                  showSuccess && "text-green-60",
                )}
              />
              <Stack gap="sm" align="center" fullWidth>
                <Typography
                  variant="bodyW1"
                  className="max-w-full break-all text-center"
                >
                  {fileLabel}
                </Typography>
                <Typography
                  variant="bodyW2"
                  className="text-center text-secondary-text"
                >
                  {description ??
                    (isDragActive
                      ? "Drop your file here"
                      : `Drag and drop a ${typeLabel} file here, or`)}
                </Typography>
                {resolvedErrorMessage && (
                  <Typography
                    variant="bodyW2"
                    className="text-error max-w-full break-all text-center"
                  >
                    {resolvedErrorMessage}
                  </Typography>
                )}
                <button
                  type="button"
                  className={cn(
                    "bg-transparent p-0 text-sm font-medium text-interactive-text",
                    "hover:text-primary-text disabled:text-stroke-secondary",
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    open();
                  }}
                  disabled={disabled}
                >
                  {label}
                </button>
              </Stack>
            </Stack>
            {multiple && hasFiles && (
              <Stack gap="xs" fullWidth className="pt-4">
                {value.map((file, index) => (
                  <Stack
                    key={`${file.name}-${index}`}
                    direction="row"
                    align="center"
                    justify="between"
                    fullWidth
                    className="rounded-md bg-gray-5 px-3 py-2"
                  >
                    <Typography
                      variant="bodyW2"
                      className="min-w-0 break-all text-secondary-text"
                    >
                      {file.name}
                    </Typography>
                    {showRemove && (
                      <button
                        type="button"
                        className="text-xs font-medium text-interactive-text hover:text-primary-text"
                        onClick={(event) => {
                          event.stopPropagation();
                          onChange(value.filter((_, idx) => idx !== index));
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            accept={acceptAttr}
            disabled={disabled}
            multiple={multiple}
            onChange={handleInputChange}
          />
          {renderFooter}
        </Stack>
      </Comp>
    );
  },
);

FileUpload.displayName = "FileUpload";

export type { FileUploadProps };
